import api from './api';
import { cachedGet } from '../utils/lookupCache';
import type {
  RepairOrderCard,
  RepairOrderListItem,
  RepairOrderDetail,
  RepairCategory,
  RepairIssue,
  TireSize,
  ChassisPool,
  DriverLookup,
  EmployeeLookup,
  VendorLookup,
  GWILocation,
  RepairDispatch,
  RepairOrderDocument,
  RepairOrderPing,
  RepairOrderPingType,
  OpenPingFeed,
} from '../types/repairOrders';

export interface ListParams {
  status?: string;
  repairpart?: string;
  vendor_assigned?: boolean;
  region?: string;
  /** Comma-separated orders-scope registry fieldnames to attach as extra columns. */
  additional_fields?: string;
}

const repairOrdersService = {
  // List all repair orders
  async list(params?: ListParams, signal?: AbortSignal): Promise<{ data: RepairOrderListItem[]; count: number }> {
    const response = await api.get('/repair-orders', { params, signal });
    return response.data;
  },

  // Get distinct regions for filter
  async getRegions(): Promise<{ data: string[] }> {
    const response = await api.get('/repair-orders/lookups/regions');
    return response.data;
  },

  // Get single repair order
  async get(recno: number, signal?: AbortSignal): Promise<{ order: RepairOrderDetail; images: string[]; noteCount: number }> {
    const response = await api.get(`/repair-orders/${recno}`, { signal });
    return response.data;
  },

  // Repair-order cards for a PRO (Pro Details ROs tab)
  // `openCount` is the server's count of OPEN (OPEN/PENDING/DEFERRED/DISPATCHED)
  // cards in `data` — used for the ROs tab's red flag. Taken from the response
  // rather than derived here so the flag and the dispatch warning share one
  // definition of "open"; `count` stays the total the badge shows.
  async getByPro(pronumber: number, signal?: AbortSignal): Promise<{ data: RepairOrderCard[]; count: number; openCount?: number }> {
    const response = await api.get(`/repair-orders/by-pro/${pronumber}`, { signal });
    return response.data;
  },

  // Repair-order cards for a chassis/truck unit (equipment ROs sidebar)
  async getByEquipment(unit: string, signal?: AbortSignal): Promise<{ data: RepairOrderCard[]; count: number }> {
    const response = await api.get(`/repair-orders/by-equipment/${encodeURIComponent(unit)}`, { signal });
    return response.data;
  },

  // Create repair order
  async create(data: any): Promise<{ success: boolean; recno: number }> {
    const response = await api.post('/repair-orders', data);
    return response.data;
  },

  // Update repair order
  async update(recno: number, data: any): Promise<{ success: boolean; recno: number }> {
    const response = await api.put(`/repair-orders/${recno}`, data);
    return response.data;
  },

  // Get repair categories lookup (filtered by type and equipment)
  async getCategories(repairtype: string, repairitem: string): Promise<{ data: RepairCategory[] }> {
    const response = await api.get('/repair-orders/lookups/categories', {
      params: { repairtype, repairitem },
    });
    return response.data;
  },

  // Get repair issues lookup (optionally filtered by category)
  async getIssues(repaircategoryrecno?: number): Promise<{ data: RepairIssue[] }> {
    const params: { repaircategoryrecno?: number } = {};
    if (repaircategoryrecno) params.repaircategoryrecno = repaircategoryrecno;
    const response = await api.get('/repair-orders/lookups/issues', { params });
    return response.data;
  },

  // Static lookups below use the shared 5-min cache: RepairOrderDrawer fires
  // all seven on every drawer open (2026-08-25 sweep).
  // Get tire sizes lookup
  async getTireSizes(): Promise<{ data: TireSize[] }> {
    const response = await cachedGet<{ data: TireSize[] }>('/repair-orders/lookups/tire-sizes');
    return response.data;
  },

  // Get chassis pools lookup
  async getChassisPools(): Promise<{ data: ChassisPool[] }> {
    const response = await cachedGet<{ data: ChassisPool[] }>('/repair-orders/lookups/chassis-pools');
    return response.data;
  },

  // Get drivers lookup
  async getDrivers(): Promise<{ data: DriverLookup[] }> {
    const response = await cachedGet<{ data: DriverLookup[] }>('/repair-orders/lookups/drivers');
    return response.data;
  },

  // Get employees lookup
  async getEmployees(): Promise<{ data: EmployeeLookup[] }> {
    const response = await cachedGet<{ data: EmployeeLookup[] }>('/repair-orders/lookups/employees');
    return response.data;
  },

  // Look up order equipment by pro number
  async lookupPro(pronumber: number): Promise<{ container: string; chassisid: string; chassispool: string }> {
    const response = await api.get(`/repair-orders/lookups/pro/${pronumber}`);
    return response.data;
  },

  // Get vendors lookup
  async getVendors(): Promise<{ data: VendorLookup[] }> {
    const response = await cachedGet<{ data: VendorLookup[] }>('/repair-orders/lookups/vendors');
    return response.data;
  },

  // Get GWI yard/warehouse/facility locations
  async getGWILocations(): Promise<{ data: GWILocation[] }> {
    const response = await cachedGet<{ data: GWILocation[] }>('/repair-orders/lookups/gwi-locations');
    return response.data;
  },

  // Get customer locations
  async getCustomerLocations(): Promise<{ data: GWILocation[] }> {
    const response = await cachedGet<{ data: GWILocation[] }>('/repair-orders/lookups/customer-locations');
    return response.data;
  },

  // Get region by equipment unit number
  async getRegionByEquipment(unit: string): Promise<{ region: string }> {
    const response = await api.get(`/repair-orders/lookups/region-by-equipment/${encodeURIComponent(unit)}`);
    return response.data;
  },

  // Upsert dispatch record for a repair order
  async upsertDispatch(orderRecno: number, data: RepairDispatch): Promise<{ success: boolean; recno: number }> {
    const response = await api.put(`/repair-orders/${orderRecno}/dispatch`, data);
    return response.data;
  },

  // List documents for a repair order
  async getDocuments(orderRecno: number): Promise<{ documents: RepairOrderDocument[] }> {
    const response = await api.get(`/repair-orders/${orderRecno}/documents`);
    return response.data;
  },

  // Download a document (returns blob for saving)
  async downloadDocument(orderRecno: number, mapRecno: number): Promise<Blob> {
    const response = await api.get(
      `/repair-orders/${orderRecno}/documents/${mapRecno}/download`,
      { responseType: 'blob' }
    );
    return response.data;
  },

  // Upload a document
  async uploadDocument(
    orderRecno: number,
    file: File,
    documenttype: string,
    description?: string,
    pronumber?: number | null
  ): Promise<{ success: boolean; document: RepairOrderDocument }> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('documenttype', documenttype);
    if (description) formData.append('description', description);
    if (pronumber) formData.append('pronumber', String(pronumber));
    const response = await api.post(
      `/repair-orders/${orderRecno}/documents`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
    return response.data;
  },

  // Soft-delete a document
  async deleteDocument(orderRecno: number, mapRecno: number): Promise<{ success: boolean }> {
    const response = await api.delete(`/repair-orders/${orderRecno}/documents/${mapRecno}`);
    return response.data;
  },

  // Send repair order details to comma- or semicolon-separated vendor emails.
  async sendVendorEmail(orderRecno: number, repairemail: string): Promise<{ success: boolean; sent_to: string; cc: string[] }> {
    const response = await api.post(`/repair-orders/${orderRecno}/send-vendor-email`, { repairemail });
    return response.data;
  },

  // Get notes for a repair order
  async getNotes(orderRecno: number): Promise<{ notes: { recno: number; datetime: string; userid: string; notes: string }[] }> {
    const response = await api.get(`/repair-orders/${orderRecno}/notes`);
    return response.data;
  },

  // Add a note to a repair order
  async addNote(orderRecno: number, notes: string): Promise<{ success: boolean }> {
    const response = await api.post(`/repair-orders/${orderRecno}/notes`, { notes });
    return response.data;
  },

  // Ping maintenance for an ETA or a completion status on a repair order.
  // Idempotent: if a ping of this type is already open, the existing one comes
  // back instead of a second one being created.
  // NOTE: the request body is snake_case (request_type) while the response is
  // camelCase — that mirrors the backend; do not "tidy" it.
  async requestPing(
    recno: number,
    requestType: RepairOrderPingType,
  ): Promise<{ success: boolean; ping: RepairOrderPing }> {
    const response = await api.post(`/repair-orders/${recno}/pings`, { request_type: requestType });
    return response.data;
  },

  // Clear an open ping. 404s if it does not exist or is already cleared.
  async clearPing(recno: number, pingRecno: number): Promise<{ success: boolean }> {
    const response = await api.post(`/repair-orders/${recno}/pings/${pingRecno}/clear`, {});
    return response.data;
  },

  // Open pings for a set of repair orders, keyed by RO recno (maintenance grid).
  // The ROs tab does not need this — its cards already embed openPings.
  async getPings(
    roRecnos: number[],
    signal?: AbortSignal,
  ): Promise<{ data: Record<string, RepairOrderPing[]> }> {
    const response = await api.get('/repair-orders/pings', {
      params: { ro_recnos: roRecnos.join(',') },
      signal,
    });
    return response.data;
  },

  // Global open-ping state for the sidebar: the true open count plus the newest
  // pings. Unlike getPings above this takes no order list — the sidebar renders
  // on every page and has none. `open` may exceed `pings.length`; see the
  // OpenPingFeed doc comment.
  async getOpenPingFeed(signal?: AbortSignal): Promise<OpenPingFeed> {
    const response = await api.get('/repair-orders/pings/count', { signal });
    return response.data;
  },
};

export default repairOrdersService;
